import { extend } from 'flarum/common/extend';
import Stream from 'flarum/common/utils/Stream';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import Select from 'flarum/common/components/Select';
import FieldSet from 'flarum/common/components/FieldSet';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import app from 'flarum/forum/app';

app.initializers.add('p36-signup-questionnaire', () => {
  // Add a "Back to Maker Space" link in the forum header (right side)
  // Use a plain <a> tag (NOT Flarum's Link component) so the browser does a
  // full page navigation to the external /maker-space/ path. Flarum's Link
  // intercepts clicks and routes through the SPA router, which fails for
  // non-forum paths.
  extend(HeaderSecondary.prototype, 'items', function (items) {
    items.add(
      'p36-back-home',
      <a className="Button Button--link p36-back-home" href="/maker-space/">
        <i className="fas fa-arrow-left" />
        {' '}
        {app.translator.trans('core.forum.sign_up.p36_back_home')}
      </a>,
      100
    );
  });

  // Initialize state
  extend(SignUpModal.prototype, 'oninit', function () {
    this.p36HeardAbout = Stream('');
    this.p36Descriptor = Stream('');
    this.p36WantToMake = Stream('');
    this.p36Interests = Stream('');
    this.p36Goals = Stream([]);
    this.p36Meetup = Stream('');
    this.p36Notes = Stream('');

    this.toggleGoal = (goal, state) => {
      const current = this.p36Goals();
      if (state) {
        if (!current.includes(goal)) this.p36Goals(current.concat(goal));
      } else {
        this.p36Goals(current.filter((g) => g !== goal));
      }
    };
  });

  // PART 1 — Human check (required)
  extend(SignUpModal.prototype, 'fields', function (fields) {
    const modal = this;

    fields.add(
      'p36-about',
      <FieldSet label={app.translator.trans('core.forum.sign_up.p36_section_about')}>
        <div className="Form-group">
          <label>{app.translator.trans('core.forum.sign_up.p36_heard_about_label')}</label>
          <input
            className="FormControl"
            type="text"
            name="p36HeardAbout"
            placeholder={app.translator.trans('core.forum.sign_up.p36_heard_about_placeholder')}
            value={modal.p36HeardAbout()}
            oninput={(e) => modal.p36HeardAbout(e.target.value)}
            required
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('core.forum.sign_up.p36_descriptor_label')}</label>
          <Select
            name="p36Descriptor"
            value={modal.p36Descriptor()}
            onchange={(v) => modal.p36Descriptor(v)}
            options={{
              '': app.translator.trans('core.forum.sign_up.p36_descriptor_placeholder'),
              curious: app.translator.trans('core.forum.sign_up.p36_descriptor_curious'),
              maker: app.translator.trans('core.forum.sign_up.p36_descriptor_maker'),
              business: app.translator.trans('core.forum.sign_up.p36_descriptor_business'),
              student: app.translator.trans('core.forum.sign_up.p36_descriptor_student'),
              parent: app.translator.trans('core.forum.sign_up.p36_descriptor_parent'),
              volunteer: app.translator.trans('core.forum.sign_up.p36_descriptor_volunteer'),
              new: app.translator.trans('core.forum.sign_up.p36_descriptor_new'),
            }}
            required
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('core.forum.sign_up.p36_want_to_make_label')}</label>
          <textarea
            className="FormControl"
            name="p36WantToMake"
            rows="3"
            placeholder={app.translator.trans('core.forum.sign_up.p36_want_to_make_placeholder')}
            value={modal.p36WantToMake()}
            oninput={(e) => modal.p36WantToMake(e.target.value)}
            required
          />
        </div>
      </FieldSet>,
      5
    );

    // PART 2 — What they want from the forum (check all that apply)
    fields.add(
      'p36-goals',
      <FieldSet label={app.translator.trans('core.forum.sign_up.p36_section_goals')}>
        <div className="Form-group">
          <label>{app.translator.trans('core.forum.sign_up.p36_goals_label')}</label>
          {['ideas', 'help', 'donations', 'volunteer', 'teach', 'space', 'connect'].map((goal) => (
            <label className="p36-goal" key={goal}>
              <input
                type="checkbox"
                checked={modal.p36Goals().includes(goal)}
                onchange={(e) => modal.toggleGoal(goal, e.target.checked)}
              />
              <span>{app.translator.trans(`core.forum.sign_up.p36_goals_${goal}`)}</span>
            </label>
          ))}
        </div>
      </FieldSet>,
      4
    );

    // PART 3 — Optional details
    fields.add(
      'p36-optional',
      <FieldSet label={app.translator.trans('core.forum.sign_up.p36_section_optional')}>
        <div className="Form-group">
          <label>{app.translator.trans('core.forum.sign_up.p36_interests_label')}</label>
          <input
            className="FormControl"
            type="text"
            name="p36Interests"
            placeholder={app.translator.trans('core.forum.sign_up.p36_interests_placeholder')}
            value={modal.p36Interests()}
            oninput={(e) => modal.p36Interests(e.target.value)}
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('core.forum.sign_up.p36_meetup_label')}</label>
          <Select
            name="p36Meetup"
            value={modal.p36Meetup()}
            onchange={(v) => modal.p36Meetup(v)}
            options={{
              '': '—',
              yes: app.translator.trans('core.forum.sign_up.p36_meetup_yes'),
              maybe: app.translator.trans('core.forum.sign_up.p36_meetup_maybe'),
              online: app.translator.trans('core.forum.sign_up.p36_meetup_online'),
            }}
          />
        </div>

        <div className="Form-group">
          <label>{app.translator.trans('core.forum.sign_up.p36_notes_label')}</label>
          <textarea
            className="FormControl"
            name="p36Notes"
            rows="2"
            placeholder={app.translator.trans('core.forum.sign_up.p36_notes_placeholder')}
            value={modal.p36Notes()}
            oninput={(e) => modal.p36Notes(e.target.value)}
          />
        </div>
      </FieldSet>,
      3
    );
  });

  // Include questionnaire data in the registration request
  extend(SignUpModal.prototype, 'submitData', function (data) {
    data.p36HeardAbout = this.p36HeardAbout();
    data.p36Descriptor = this.p36Descriptor();
    data.p36WantToMake = this.p36WantToMake();
    data.p36Interests = this.p36Interests();
    data.p36Goals = this.p36Goals().join(',');
    data.p36Meetup = this.p36Meetup();
    data.p36Notes = this.p36Notes();
  });
});
